import { useState, useRef, useCallback } from 'react';

const TOTAL_SECONDS = 86400; // 24h
const HOURS = Array.from({ length: 9 }, (_, i) => i * 3); // 0, 3, 6, ..., 24

function timeToSeconds(datumCas) {
    if (!datumCas) return 0;
    const t = datumCas.slice(11); // "HH:MM:SS"
    const [h, m, s] = t.split(':').map(Number);
    return h * 3600 + m * 60 + (s || 0);
}

function formatTime(totalSeconds) {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function ZaznamyTimeline({ zaznamy = [], onSeek, currentTime = -1, sectionColor, isDark }) {
    const barRef = useRef(null);
    const [hoverX, setHoverX] = useState(null);
    const [hoverTime, setHoverTime] = useState(null);

    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';

    // Segmenty z záznamů
    const segments = zaznamy
        .filter(z => z.datum_cas && z.trvani_s)
        .map(z => {
            const start = timeToSeconds(z.datum_cas);
            const duration = z.trvani_s;
            return { start, duration, end: start + duration, soubor: z.soubor };
        })
        .sort((a, b) => a.start - b.start);

    const getSecondsFromEvent = useCallback((e) => {
        const bar = barRef.current;
        if (!bar) return 0;
        const rect = bar.getBoundingClientRect();
        const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
        return (x / rect.width) * TOTAL_SECONDS;
    }, []);

    const handleClick = useCallback((e) => {
        const secs = getSecondsFromEvent(e);
        onSeek?.(secs);
    }, [getSecondsFromEvent, onSeek]);

    const handleMouseMove = useCallback((e) => {
        const bar = barRef.current;
        if (!bar) return;
        const rect = bar.getBoundingClientRect();
        const x = e.clientX - rect.left;
        setHoverX(x);
        setHoverTime(formatTime((x / rect.width) * TOTAL_SECONDS));
    }, []);

    const handleMouseLeave = useCallback(() => {
        setHoverX(null);
        setHoverTime(null);
    }, []);

    const playheadPct = currentTime >= 0 ? (currentTime / TOTAL_SECONDS) * 100 : -1;

    return (
        <div style={{ userSelect: 'none' }}>
            {/* Bar */}
            <div ref={barRef}
                onClick={handleClick}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                style={{
                    position: 'relative', height: 36, borderRadius: 6, overflow: 'hidden', cursor: 'pointer',
                    background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                }}>
                {/* Hodinové čáry */}
                {Array.from({ length: 24 }, (_, i) => i).map(h => (
                    <div key={h} style={{
                        position: 'absolute', top: 0, bottom: 0,
                        left: `${(h / 24) * 100}%`,
                        width: 1,
                        background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                    }} />
                ))}

                {/* Segmenty záznamů */}
                {segments.map((seg, i) => (
                    <div key={i} style={{
                        position: 'absolute', top: 2, bottom: 2,
                        left: `${(seg.start / TOTAL_SECONDS) * 100}%`,
                        width: `${(seg.duration / TOTAL_SECONDS) * 100}%`,
                        background: `${sectionColor}60`,
                        borderRadius: 3,
                        minWidth: 2,
                        border: `1px solid ${sectionColor}40`,
                    }} />
                ))}

                {/* Playhead */}
                {playheadPct >= 0 && playheadPct <= 100 && (
                    <div style={{
                        position: 'absolute', top: 0, bottom: 0,
                        left: `${playheadPct}%`,
                        width: 2, background: '#fff',
                        boxShadow: '0 0 6px rgba(0,0,0,0.5)',
                        zIndex: 2, pointerEvents: 'none',
                    }} />
                )}

                {/* Hover indicator */}
                {hoverX !== null && (
                    <>
                        <div style={{
                            position: 'absolute', top: 0, bottom: 0,
                            left: hoverX, width: 1,
                            background: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                            pointerEvents: 'none', zIndex: 3,
                        }} />
                        <div style={{
                            position: 'absolute', top: -28,
                            left: hoverX, transform: 'translateX(-50%)',
                            background: isDark ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.75)',
                            color: '#fff', fontSize: 11, fontWeight: 600,
                            padding: '3px 8px', borderRadius: 5,
                            pointerEvents: 'none', zIndex: 4, whiteSpace: 'nowrap',
                        }}>
                            {hoverTime}
                        </div>
                    </>
                )}
            </div>

            {/* Časové popisky */}
            <div style={{ position: 'relative', height: 18, marginTop: 4 }}>
                {HOURS.map(h => (
                    <span key={h} style={{
                        position: 'absolute',
                        left: `${(h / 24) * 100}%`,
                        transform: h === 0 ? 'none' : h === 24 ? 'translateX(-100%)' : 'translateX(-50%)',
                        fontSize: 10, color: mutedColor, fontVariantNumeric: 'tabular-nums',
                    }}>
                        {String(h).padStart(2, '0')}:00
                    </span>
                ))}
            </div>
        </div>
    );
}

export { timeToSeconds };
